<template>
  <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
    <div class="text-[#414141] font-sans">
      <div v-if="tabs.length === 0" class="text-center py-8 text-gray-500">
        No outcomes found. Please add outcomes in Module 3 first.
      </div>
      <div v-else>
        <div class="mb-10">
          <div class="flex space-x-10 border-b border-[#D9DDE5] overflow-x-auto">
            <button
              v-for="(tab, index) in tabs"
              :key="tab.slug"
              @click="activeTab = index"
              :class="[
                'pb-2 text-[14px] leading-[160%] whitespace-nowrap border-b-[3px] -mb-px transition-colors',
                activeTab === index
                  ? 'border-[#00423D] text-[#00423D] font-semibold'
                  : 'border-transparent text-[#667085] font-normal hover:text-[#00423D]'
              ]"
            >
              {{ tab.name }}
            </button>
          </div>
        </div>

        <div v-if="isLoading" class="py-12 text-center text-[14px] text-[#414141]">
          Loading outcome details...
        </div>

        <div v-else-if="errorMessage" class="rounded-xl border border-[#F1B5B5] bg-[#FFF7F7] px-4 py-3 text-[14px] text-[#851208]">
          {{ errorMessage }}
        </div>

        <div v-else-if="detailOutcome" class="space-y-10 pb-4">
          <section>
            <h3 class="mb-4 text-[20px] leading-[140%] font-semibold text-[#00423D]">Outcome Description</h3>
            <p class="max-w-[1100px] text-[14px] leading-[160%] text-[#2F2F2F]">
              {{ detailOutcome.description || 'No description available for this outcome yet.' }}
            </p>
          </section>

          <section>
            <h3 class="mb-5 text-[20px] leading-[140%] font-semibold text-[#414141]">Core Annual Program(s)</h3>

            <div v-if="corePrograms.length === 0" class="rounded-[20px] bg-[#F5F6FA] px-6 py-8 text-[14px] leading-[160%] text-[#414141]">
              No core annual programs are linked to this outcome yet.
            </div>

            <div v-else class="rounded-[20px] bg-[#F5F6FA] px-8 py-6">
              <div
                v-for="(program, index) in corePrograms"
                :key="program.localKey"
                class="border-b border-[#E0E0E04A] last:border-b-0"
              >
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-4 py-5 text-left"
                  @click="toggleProgram(program.localKey)"
                >
                  <div class="min-w-0 flex-1">
                    <span class="block text-[14px] leading-[160%] font-semibold uppercase text-[#00423D]">
                      {{ (index + 1) + "- " + program.title }}
                    </span>
                    <p
                      v-if="program.description"
                      class="mt-2 text-[14px] leading-[160%] font-normal normal-case text-[#414141]"
                    >
                      {{ program.description }}
                    </p>
                  </div>
                  <img
                    :src="openProgramKey === program.localKey ? '/images/up.svg' : '/images/down.svg'"
                    :alt="openProgramKey === program.localKey ? 'Collapse section' : 'Expand section'"
                    class="h-3.5 w-3.5 flex-shrink-0"
                  />
                </button>

                <div v-if="openProgramKey === program.localKey" class="pb-6">
                  <div v-if="program.kpis.length === 0" class="rounded-[12px] border border-[#00423D] bg-[linear-gradient(0deg,rgba(245,246,250,0.7),rgba(245,246,250,0.7)),linear-gradient(0deg,#FFFFFF,#FFFFFF)] px-6 py-8 text-[12px] leading-[160%] text-[#414141]">
                    No KPIs are available for this core annual program yet.
                  </div>

                  <div v-else class="outcome-kpi-table">
                    <div class="outcome-kpi-header-row">
                      <div class="outcome-kpi-header outcome-kpi-header-left">KPI Title</div>
                      <div class="outcome-kpi-header outcome-kpi-header-left outcome-kpi-left-divider">KPI Description</div>
                      <div class="outcome-kpi-header outcome-kpi-header-right outcome-kpi-right-start">Target KPI</div>
                      <div class="outcome-kpi-header outcome-kpi-header-right outcome-kpi-right-divider outcome-kpi-right-end">Actual Results Achieved</div>
                    </div>

                    <div class="outcome-kpi-body">
                      <div class="outcome-kpi-results-panel"></div>
                      <div
                        v-for="(kpi, kpiIndex) in program.kpis"
                        :key="kpi.localKey"
                        class="outcome-kpi-row"
                      >
                        <div class="outcome-kpi-cell outcome-kpi-left-cell" :class="{ 'outcome-kpi-left-last-start': kpiIndex === program.kpis.length - 1 }">
                          {{ kpi.title || '-' }}
                        </div>
                        <div class="outcome-kpi-cell outcome-kpi-left-cell outcome-kpi-left-divider" :class="{ 'outcome-kpi-left-last-end': kpiIndex === program.kpis.length - 1 }">
                          {{ kpi.description || '-' }}
                        </div>
                        <div class="outcome-kpi-cell outcome-kpi-right-cell outcome-kpi-right-start">
                          {{ kpi.targetMetric || '-' }}
                        </div>
                        <div class="outcome-kpi-cell outcome-kpi-right-cell outcome-kpi-right-divider outcome-kpi-right-end">
                          <textarea
                            v-model="actualResultsByKpi[kpi.localKey]"
                            @input="handleActualResultsInput(program, kpi)"
                            rows="3"
                            class="outcome-kpi-textarea"
                            placeholder="Write here"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isLoading = ref(false);
const errorMessage = ref('');
const detailOutcome = ref(null);
const corePrograms = ref([]);
const openProgramKey = ref(null);
const actualResultsByKpi = ref({});
const autoSaveTimer = ref(null);
const pendingSaveKpiKey = ref(null);
const kpiSnapshots = ref({});

const slugifyOutcome = (title) => title
  ?.toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || '';

const outcomes = computed(() => {
  const storedOutcomes = JSON.parse(localStorage.getItem('outcomes') || '[]');
  return storedOutcomes.map((outcome) => ({
    ...outcome,
    slug: slugifyOutcome(outcome.title),
  })).filter((outcome) => outcome.slug);
});

const tabs = computed(() => outcomes.value.map((outcome) => ({
  name: outcome.title,
  slug: outcome.slug,
  outcomeId: outcome.id,
})));

const activeTab = computed({
  get() {
    const activeIndex = tabs.value.findIndex((tab) => tab.slug === route.params.slug);
    return activeIndex === -1 ? 0 : activeIndex;
  },
  set(index) {
    const tab = tabs.value[index];
    if (tab && tab.slug !== route.params.slug) {
      router.push(`/steps/core-program-evaluation/outcome/${tab.slug}`);
    }
  },
});

const activeOutcome = computed(() => outcomes.value.find((outcome) => outcome.slug === route.params.slug) || null);

const authHeaders = computed(() => ({
  Authorization: `Bearer ${authStore.user.accessToken}`,
  'Content-Type': 'application/json',
}));

const normalizeKpi = (kpi, programKey, index) => ({
  id: kpi.id ?? null,
  localKey: `${programKey}-kpi-${kpi.id ?? index}`,
  title: kpi.title ?? '',
  description: kpi.description ?? '',
  baselineMetric: kpi.baseline_metric ?? '',
  targetMetric: kpi.kpi_target_metric ?? '',
  targetDate: kpi.target_date ?? '',
  actualResultsAchieved: kpi.actual_results_achieved ?? '',
  assignedUserIds: Array.isArray(kpi.assigned_user_ids)
    ? [...kpi.assigned_user_ids]
    : Array.isArray(kpi.assigned_users)
      ? kpi.assigned_users.map((user) => user.id).filter(Boolean)
      : [],
});

const normalizeProgram = (program, index) => {
  const localKey = `program-${program.id ?? index}`;
  const kpis = Array.isArray(program.kpis)
    ? program.kpis.map((kpi, kpiIndex) => normalizeKpi(kpi, localKey, kpiIndex))
    : [];

  return {
    id: program.id ?? null,
    localKey,
    title: program.program_name ?? program.title ?? '-',
    description: program.program_description ?? program.description ?? '',
    kpis,
  };
};

const hydrateActualResults = (programs) => {
  const nextState = {};
  programs.forEach((program) => {
    program.kpis.forEach((kpi) => {
      nextState[kpi.localKey] = kpi.actualResultsAchieved;
    });
  });
  actualResultsByKpi.value = nextState;
};

const getKpiPayload = (kpi) => ({
  id: kpi.id || undefined,
  title: kpi.title?.trim() || '',
  description: kpi.description?.trim() || '',
  baseline_metric: kpi.baselineMetric?.trim() || '',
  kpi_target_metric: kpi.targetMetric?.trim() || '',
  target_date: kpi.targetDate || null,
  actual_results_achieved: actualResultsByKpi.value[kpi.localKey]?.trim() || '',
  assigned_user_ids: Array.isArray(kpi.assignedUserIds) ? [...kpi.assignedUserIds] : [],
});

const getKpiSnapshotKey = (kpi) => JSON.stringify(getKpiPayload(kpi));

const refreshKpiSnapshots = (programs) => {
  const nextSnapshots = {};
  programs.forEach((program) => {
    program.kpis.forEach((kpi) => {
      nextSnapshots[kpi.localKey] = getKpiSnapshotKey(kpi);
    });
  });
  kpiSnapshots.value = nextSnapshots;
};

const hasKpiChanged = (kpi) => kpiSnapshots.value[kpi.localKey] !== getKpiSnapshotKey(kpi);

const setKpiSnapshot = (kpi) => {
  kpiSnapshots.value = {
    ...kpiSnapshots.value,
    [kpi.localKey]: getKpiSnapshotKey(kpi),
  };
};

const saveKpiActualResults = async (program, kpi, { silent = false } = {}) => {
  if (!program?.id || !kpi?.id || !authStore.user?.accessToken) {
    return false;
  }

  if (!hasKpiChanged(kpi)) {
    return true;
  }

  try {
    if (!silent) {
      errorMessage.value = '';
    }

    const response = await fetch(`${global.API_URL}/kpis/core_program/${program.id}`, {
      method: 'POST',
      headers: authHeaders.value,
      body: JSON.stringify({
        kpi: getKpiPayload(kpi),
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    const savedKpi = normalizeKpi(data.kpi || {}, program.localKey, 0);

    corePrograms.value = corePrograms.value.map((currentProgram) => {
      if (currentProgram.localKey !== program.localKey) {
        return currentProgram;
      }

      return {
        ...currentProgram,
        kpis: currentProgram.kpis.map((currentKpi) => currentKpi.localKey === kpi.localKey
          ? { ...savedKpi, localKey: currentKpi.localKey }
          : currentKpi),
      };
    });

    actualResultsByKpi.value = {
      ...actualResultsByKpi.value,
      [kpi.localKey]: savedKpi.actualResultsAchieved,
    };

    const nextKpi = { ...savedKpi, localKey: kpi.localKey };
    setKpiSnapshot(nextKpi);
    return true;
  } catch (error) {
    console.error('Failed to save KPI actual results:', error);
    if (!silent) {
      errorMessage.value = 'Failed to save actual results achieved. Please try again.';
    }
    return false;
  }
};

const scheduleAutoSave = (program, kpi) => {
  if (autoSaveTimer.value) {
    clearTimeout(autoSaveTimer.value);
  }

  pendingSaveKpiKey.value = { programKey: program.localKey, kpiKey: kpi.localKey };
  autoSaveTimer.value = setTimeout(async () => {
    const pending = pendingSaveKpiKey.value;
    pendingSaveKpiKey.value = null;
    if (!pending) return;

    const pendingProgram = corePrograms.value.find((item) => item.localKey === pending.programKey);
    const pendingKpi = pendingProgram?.kpis.find((item) => item.localKey === pending.kpiKey);

    if (pendingProgram && pendingKpi) {
      await saveKpiActualResults(pendingProgram, pendingKpi, { silent: true });
    }
  }, 800);
};

const handleActualResultsInput = (program, kpi) => {
  errorMessage.value = '';
  scheduleAutoSave(program, kpi);
};

const flushPendingAutoSave = async () => {
  if (autoSaveTimer.value) {
    clearTimeout(autoSaveTimer.value);
    autoSaveTimer.value = null;
  }

  const pending = pendingSaveKpiKey.value;
  pendingSaveKpiKey.value = null;

  if (!pending) {
    return true;
  }

  const pendingProgram = corePrograms.value.find((item) => item.localKey === pending.programKey);
  const pendingKpi = pendingProgram?.kpis.find((item) => item.localKey === pending.kpiKey);

  if (!pendingProgram || !pendingKpi) {
    return true;
  }

  return saveKpiActualResults(pendingProgram, pendingKpi);
};

const toggleProgram = (programKey) => {
  openProgramKey.value = openProgramKey.value === programKey ? null : programKey;
};

const loadOutcomeDetails = async () => {
  if (!activeOutcome.value?.id || !authStore.user?.accessToken) {
    detailOutcome.value = activeOutcome.value;
    corePrograms.value = [];
    openProgramKey.value = null;
    actualResultsByKpi.value = {};
    kpiSnapshots.value = {};
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await fetch(`${global.API_URL}/outcomes/${activeOutcome.value.id}/details`, {
      headers: authHeaders.value,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    const payloadOutcome = data.outcome || activeOutcome.value;
    const payloadPrograms = Array.isArray(data.core_programs)
      ? data.core_programs
      : Array.isArray(data.corePrograms)
        ? data.corePrograms
        : Array.isArray(data.outcome?.core_programs)
          ? data.outcome.core_programs
          : [];

    detailOutcome.value = {
      ...activeOutcome.value,
      ...payloadOutcome,
    };

    const normalizedPrograms = payloadPrograms.map((program, index) => normalizeProgram(program, index));
    corePrograms.value = normalizedPrograms;
    openProgramKey.value = normalizedPrograms[0]?.localKey || null;
    hydrateActualResults(normalizedPrograms);
    refreshKpiSnapshots(normalizedPrograms);
  } catch (error) {
    console.error('Failed to fetch outcome details:', error);
    detailOutcome.value = activeOutcome.value;
    corePrograms.value = [];
    openProgramKey.value = null;
    actualResultsByKpi.value = {};
    kpiSnapshots.value = {};
    errorMessage.value = 'Failed to load outcome KPI details. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const validateCurrentTab = async () => flushPendingAutoSave();

watch(
  () => activeOutcome.value?.id,
  async (_, __, onCleanup) => {
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });

    await flushPendingAutoSave();
    if (!cancelled) {
      loadOutcomeDetails();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  flushPendingAutoSave();
});

defineExpose({
  activeTab,
  tabs,
  validateCurrentTab,
  isRouteDrivenTabs: true,
});
</script>

<style scoped>
.outcome-kpi-table {
  width: 100%;
}

.outcome-kpi-header-row,
.outcome-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  width: 100%;
}

.outcome-kpi-header {
  padding: 18px 24px;
  display: flex;
  align-items: center;
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 105%;
  letter-spacing: 0;
  text-transform: uppercase;
}

.outcome-kpi-header-left {
  color: #00423d;
}

.outcome-kpi-left-divider {
  border-left: 1px solid rgba(224, 224, 224, 0.29);
}

.outcome-kpi-header-right {
  background: #00423d;
  color: #ffffff;
  border-top: 1px solid #00423d;
}

.outcome-kpi-right-start {
  border-left: 1px solid #00423d;
}

.outcome-kpi-right-end {
  border-right: 1px solid #00423d;
}

.outcome-kpi-right-divider {
  border-left: 1px solid rgba(224, 224, 224, 0.29);
}

.outcome-kpi-header-row .outcome-kpi-right-start {
  border-top-left-radius: 12px;
}

.outcome-kpi-header-row .outcome-kpi-right-end {
  border-top-right-radius: 12px;
}

.outcome-kpi-body {
  position: relative;
}

.outcome-kpi-results-panel {
  position: absolute;
  top: -12px;
  left: 50%;
  right: 0;
  bottom: 0;
  border: 1px solid #00423d;
  border-radius: 12px;
  background: #ffffff;
  pointer-events: none;
  z-index: 0;
}

.outcome-kpi-cell {
  min-height: 96px;
  padding: 18px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 105%;
  letter-spacing: 0;
  color: #414141;
  position: relative;
  z-index: 1;
}

.outcome-kpi-left-cell {
  background: #ffffff;
  border-top: 1px solid rgba(224, 224, 224, 0.29);
}

.outcome-kpi-left-last-start {
  border-bottom-left-radius: 12px;
}

.outcome-kpi-left-last-end {
  border-bottom-right-radius: 12px;
}

.outcome-kpi-right-cell {
  background: transparent;
}

.outcome-kpi-right-cell.outcome-kpi-right-start {
  border-left: 0;
  border-top: 0;
}

.outcome-kpi-right-cell.outcome-kpi-right-end {
  border-right: 0;
  border-top: 0;
}

.outcome-kpi-row + .outcome-kpi-row .outcome-kpi-right-cell {
  border-top: 1px solid rgba(224, 224, 224, 0.29);
}

.outcome-kpi-textarea {
  width: 100%;
  max-width: 100%;
  min-height: 64px;
  resize: none;
  border: 0;
  background: transparent;
  padding: 0;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 105%;
  letter-spacing: 0;
  color: #414141;
  outline: none;
}

.outcome-kpi-textarea::placeholder {
  color: #8b8b8b;
  font-style: italic;
}
</style>
